import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Breakpoint } from '@builder4/shared/types/layout';
import { DESKTOP_WIDTH, TABLET_WIDTH, MOBILE_WIDTH } from '@builder4/shared/utils/constants';

export const DESKTOP: Breakpoint = 'desktop';
export const TABLET: Breakpoint = 'tablet';
export const MOBILE: Breakpoint = 'mobile';

export interface SystemState {
  breakpoint: Breakpoint;
  viewportBreakpoint: Breakpoint;
  pageWidth: number;
  pageHeight: number;
  isObjectResizing: boolean;
  previewModeEnabled: boolean;
  analyticsViewEnabled: boolean;
  panelDragging: boolean;
  isNavbarTitleDropdownOpen: boolean;
  isFullscreenPreview: boolean;
}

// Breakpoint width mappings for all design breakpoints
export const BREAKPOINT_WIDTHS: Record<Breakpoint, number> = {
  desktop: DESKTOP_WIDTH,
  tablet: TABLET_WIDTH,
  mobile: MOBILE_WIDTH,
};

const initialState: SystemState = {
  breakpoint: 'desktop',
  viewportBreakpoint: 'desktop',
  pageWidth: BREAKPOINT_WIDTHS['desktop'],
  pageHeight: 800,
  isObjectResizing: false,
  previewModeEnabled: false,
  analyticsViewEnabled: false,
  panelDragging: false,
  isNavbarTitleDropdownOpen: false,
  isFullscreenPreview: false,
};
const systemSlice = createSlice({
  name: 'system',
  initialState,
  reducers: {
    setBreakpoint(state, action: PayloadAction<Breakpoint>) {
      state.breakpoint = action.payload;
      state.pageWidth = BREAKPOINT_WIDTHS[action.payload];
    },
    setPageDimensions(state, action: PayloadAction<{ width: number; height: number }>) {
      state.pageWidth = action.payload.width;
      state.pageHeight = action.payload.height;
    },
    setObjectResizing(state, action: PayloadAction<boolean>) {
      state.isObjectResizing = action.payload;
    },
    setPreviewModeEnabled(state, action: PayloadAction<boolean>) {
      state.previewModeEnabled = action.payload;
    },
    setAnalyticsViewEnabled(state, action: PayloadAction<boolean>) {
      state.analyticsViewEnabled = action.payload;
    },
    setPanelDragging(state, action: PayloadAction<boolean>) {
      state.panelDragging = action.payload;
    },
    setNavbarTitleDropdownOpen(state, action: PayloadAction<boolean>) {
      state.isNavbarTitleDropdownOpen = action.payload;
    },
    setFullscreenPreview(state, action: PayloadAction<boolean>) {
      state.isFullscreenPreview = action.payload;
    },
    setViewportBreakpoint(state, action: PayloadAction<Breakpoint>) {
      state.viewportBreakpoint = action.payload;
    },
  },
});

export const {
  setPreviewModeEnabled,
  setAnalyticsViewEnabled,
  setBreakpoint,
  setPageDimensions,
  setObjectResizing,
  setPanelDragging,
  setNavbarTitleDropdownOpen,
  setFullscreenPreview,
  setViewportBreakpoint,
} = systemSlice.actions;
export default systemSlice.reducer;
